import React, { useState, useEffect } from 'react';
import { Smartphone, Download, Star, Target, BarChart3, ChevronDown, ChevronUp, Lightbulb, TrendingUp, Award, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MobileAppSection: React.FC = () => {
  const { t } = useTranslation();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-scroll effect
  useEffect(() => {
    if (!isAutoScrolling || isPaused) return;

    const interval = setInterval(() => {
      setScrollPosition(prev => {
        if (prev >= 100) {
          setTimeout(() => setScrollPosition(0), 1000);
          return 100;
        }
        return prev + 0.3;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isAutoScrolling, isPaused]);

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const resetScroll = () => {
    setScrollPosition(0);
    setIsPaused(false);
  };

  return (
    <section id="mobile-app" className="py-20 relative overflow-hidden">
      {/* Dégradé radial simple et beau */}
      <div className="absolute inset-0 bg-gradient-radial from-cyan-400 via-blue-500 to-indigo-600"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left relative z-10">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('landing.mobile_app.title')}
            </h2>
            
            <p className="text-xl text-white mb-8 leading-relaxed">
              {t('landing.mobile_app.subtitle')}
            </p>
            
            <p className="text-white mb-8 leading-relaxed">
              {t('landing.mobile_app.description')}
            </p>
            
            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <a
                href="https://apps.apple.com/us/app/cv-match/id6752223198"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center px-6 py-4 bg-black text-white rounded-2xl hover:bg-gray-900 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <div className="w-8 h-8 mr-4 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-xs text-gray-300">{t('landing.mobile_app.download_buttons.app_store.download_on')}</div>
                  <div className="text-lg font-semibold">{t('landing.mobile_app.download_buttons.app_store.store_name')}</div>
                </div>
              </a>
              
              <a
                href="https://play.google.com/store/apps/details?id=com.cv_analyzer"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center px-6 py-4 bg-black text-white rounded-2xl hover:bg-gray-900 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <div className="w-8 h-8 mr-4 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-xs text-gray-300">{t('landing.mobile_app.download_buttons.google_play.available_on')}</div>
                  <div className="text-lg font-semibold">{t('landing.mobile_app.download_buttons.google_play.store_name')}</div>
                </div>
              </a>
            </div>
          </div>
          
          {/* Interactive Mobile Mockup */}
          <div className="relative flex justify-center lg:justify-end z-10">
            <div className="relative transform perspective-1000 hover:scale-105 transition-all duration-700">
              {/* Halo lumineux principal */}
              <div className="absolute -inset-8 bg-gradient-to-r from-cyan-400/30 via-blue-500/40 to-indigo-600/30 rounded-[4rem] blur-2xl animate-pulse"></div>
              
              {/* Halo secondaire plus large */}
              <div className="absolute -inset-12 bg-gradient-to-r from-cyan-300/20 via-blue-400/25 to-indigo-500/20 rounded-[5rem] blur-3xl opacity-60 animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              {/* Reflets latéraux */}
              <div className="absolute -left-6 top-1/4 w-2 h-1/2 bg-gradient-to-b from-transparent via-cyan-400/50 to-transparent blur-sm"></div>
              <div className="absolute -right-6 top-1/3 w-2 h-1/3 bg-gradient-to-b from-transparent via-blue-400/50 to-transparent blur-sm"></div>
              
              {/* Phone Container */}
              <div className="relative w-80 h-[650px] bg-white rounded-[3rem] shadow-2xl overflow-hidden border-8 border-gray-200 transform hover:rotateY-6 transition-all duration-500 hover:shadow-cyan-500/25 hover:shadow-[0_0_50px_rgba(34,211,238,0.4)] drop-shadow-[0_35px_35px_rgba(0,0,0,0.25)] hover:drop-shadow-[0_45px_65px_rgba(34,211,238,0.4)]">
                {/* Reflet sur l'écran */}
                <div className="absolute inset-2 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-[2.5rem] pointer-events-none"></div>
                
                {/* Status Bar */}
                <div className="absolute top-0 left-0 right-0 h-12 bg-white flex items-center justify-between px-8 pt-2 z-10">
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-full"></div>
                  <span className="text-black font-semibold text-sm mt-8">{t('landing.mobile_app.mobile_interface.time')}</span>
                  <div className="flex items-center space-x-1 mt-8">
                    <div className="flex space-x-1">
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-black rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    </div>
                    <svg className="w-6 h-3" viewBox="0 0 24 12" fill="none">
                      <rect x="1" y="3" width="18" height="6" rx="2" stroke="black" strokeWidth="1" fill="none"/>
                      <rect x="2" y="4" width="14" height="4" rx="1" fill="black"/>
                      <rect x="20" y="5" width="2" height="2" rx="0.5" fill="black"/>
                    </svg>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="pt-20 h-full bg-gradient-to-br from-indigo-50 via-white to-purple-50 overflow-hidden">
                  <div 
                    className="transition-transform duration-1000 ease-out h-[1260px]"
                    style={{ transform: `translateY(-${Math.min(scrollPosition, 50)}%)` }}
                  >
                    {/* Screen 1: Main Dashboard */}
                    <div className="h-[630px] px-6 py-8 flex flex-col justify-center">
                      {/* Header */}
                      <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
                          <div className="text-center">
                            <div className="text-2xl font-black bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent">
                              CV
                            </div>
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-black mb-3">
                          {t('landing.mobile_app.mobile_interface.app_name')}
                        </h3>
                        <p className="text-xs text-gray-500 font-medium tracking-wider mb-3">
                          {t('landing.mobile_app.mobile_interface.country')}
                        </p>
                        <p className="text-gray-600 text-sm">
                          {t('landing.mobile_app.mobile_interface.tagline')}
                        </p>
                      </div>
                      
                      {/* Score Display */}
                      <div className="bg-white rounded-3xl p-6 shadow-lg mb-8">
                        <div className="text-center mb-6">
                          <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                            87%
                          </div>
                          <p className="text-gray-600 text-sm">{t('landing.mobile_app.mobile_interface.compatibility_score')}</p>
                        </div>
                        
                        {/* Progress bars */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">{t('landing.mobile_app.mobile_interface.skills')}</span>
                            <span className="font-semibold text-blue-600">92%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                          </div>
                          
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">{t('landing.mobile_app.mobile_interface.experience')}</span>
                            <span className="font-semibold text-orange-600">78%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-orange-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                          </div>
                          
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">{t('landing.mobile_app.mobile_interface.education')}</span>
                            <span className="font-semibold text-green-600">85%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl py-3 px-4 text-center font-semibold shadow-lg mb-8">
                        <span>{t('landing.mobile_app.mobile_interface.see_recommendations')}</span>
                      </div>
                      
                      {/* Features */}
                      <div className="space-y-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <BarChart3 className="w-4 h-4 mr-3 text-indigo-500" />
                          <span>{t('landing.mobile_app.mobile_interface.detailed_analysis_by_ai')}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Star className="w-4 h-4 mr-3 text-purple-500" />
                          <span>{t('landing.mobile_app.mobile_interface.personalized_recommendations')}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Download className="w-4 h-4 mr-3 text-green-500" />
                          <span>{t('landing.mobile_app.mobile_interface.downloadable_pdf_report')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Screen 2: Detailed Analysis */}
                    <div className="h-[630px] px-6 py-8 flex flex-col justify-center">
                      {/* Header with back button */}
                      <div className="flex items-center mb-6">
                        <ChevronUp className="w-6 h-6 text-gray-600 mr-2" />
                        <h3 className="text-lg font-bold text-black">{t('landing.mobile_app.mobile_interface.detailed_analysis')}</h3>
                      </div>

                      {/* Detailed Scores */}
                      <div className="space-y-4 mb-6">
                        <div className="bg-white rounded-xl p-3 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <Award className="w-5 h-5 text-blue-500 mr-2" />
                              <span className="font-semibold text-gray-800 text-sm">{t('landing.mobile_app.mobile_interface.skills')}</span>
                            </div>
                            <span className="text-blue-600 font-bold">92%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{t('landing.mobile_app.mobile_interface.excellent_match')}</p>
                        </div>

                        <div className="bg-white rounded-xl p-3 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <TrendingUp className="w-5 h-5 text-orange-500 mr-2" />
                              <span className="font-semibold text-gray-800 text-sm">{t('landing.mobile_app.mobile_interface.experience')}</span>
                            </div>
                            <span className="text-orange-600 font-bold">78%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-orange-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{t('landing.mobile_app.mobile_interface.good_match')}</p>
                        </div>

                        <div className="bg-white rounded-xl p-3 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <BookOpen className="w-5 h-5 text-green-500 mr-2" />
                              <span className="font-semibold text-gray-800 text-sm">{t('landing.mobile_app.mobile_interface.education')}</span>
                            </div>
                            <span className="text-green-600 font-bold">85%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{t('landing.mobile_app.mobile_interface.very_suitable')}</p>
                        </div>
                      </div>

                      {/* Recommendations */}
                      <div className="bg-indigo-50 rounded-xl p-4">
                        <div className="flex items-center mb-3">
                          <Lightbulb className="w-5 h-5 text-indigo-600 mr-2" />
                          <h4 className="font-semibold text-indigo-800 text-sm">{t('landing.mobile_app.mobile_interface.ai_recommendations')}</h4>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2 mt-1.5"></div>
                            <p className="text-xs text-gray-700">{t('landing.mobile_app.mobile_interface.add_quantifiable_metrics')}</p>
                          </div>
                          <div className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2 mt-1.5"></div>
                            <p className="text-xs text-gray-700">{t('landing.mobile_app.mobile_interface.highlight_team_management')}</p>
                          </div>
                          <div className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2 mt-1.5"></div>
                            <p className="text-xs text-gray-700">{t('landing.mobile_app.mobile_interface.add_certifications')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileAppSection;